import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getPasswordManagerData, getPin as getRemotePin, migrateSingleNoteIfAny, listEntries, addEntry, updateEntry, deleteEntry, type CredentialEntry, type ThemeVariant } from '../services/passwordManagerService';
import {getAuth} from 'firebase/auth';

interface PasswordManagerModalProps {
  onClose: () => void;
}

const PIN_STORAGE_KEY = 'passwordManager.pin';
const DATA_STORAGE_KEY = 'passwordManager.data';
const DEFAULT_PIN = '1234';
const PIN_SESSION_KEY_PREFIX = 'passwordManager.pinVerified.'; // per-user session key
const PIN_SESSION_TTL_MS = 60 * 60 * 1000; // 60 minutes

const PasswordManagerModal: React.FC<PasswordManagerModalProps> = ({ onClose }) => {
  const [showModal, setShowModal] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [query, setQuery] = useState('');
  const [entries, setEntries] = useState<CredentialEntry[]>([]);
  const [editing, setEditing] = useState<CredentialEntry | null>(null);
  const [viewing, setViewing] = useState<CredentialEntry | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<CredentialEntry | null>(null);
  const [savedIndicator, setSavedIndicator] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const variants: ThemeVariant[] = ['primary','success','warning','info','danger','secondary'];
  const [revealedMap, setRevealedMap] = useState<Record<string, boolean>>({});
  const maskPassword = (pw: string, reveal: boolean): string => {
    if (!pw) return '(none)';
    if (reveal) return pw;
    if (pw.length >= 4) {
      return `${pw.slice(0, 2)}••••${pw.slice(-2)}`;
    }
    return '••••';
  };

  const getCardClassesForVariant = (v?: ThemeVariant): string => {
    const variant = v || 'primary';
    // Use standard Metronic variant classes that adapt to light/dark themes automatically
    switch (variant) {
      case 'success':
        return 'bg-success bg-opacity-10 border-success';
      case 'warning':
        return 'bg-warning bg-opacity-10 border-warning';
      case 'info':
        return 'bg-info bg-opacity-10 border-info';
      case 'danger':
        return 'bg-danger bg-opacity-10 border-danger';
      case 'secondary':
        return 'bg-secondary bg-opacity-10 border-secondary';
      case 'primary':
      default:
        return 'bg-primary bg-opacity-10 border-primary';
    }
  };

  // Resolve current user id (fallback to local storage pattern used elsewhere)
  const getUserId = (): string => {
    // Prefer Firebase Auth UID (most reliable, matches Firestore security model)
    try {
      const uid = getAuth().currentUser?.uid;
      if (uid) return uid;
    } catch {}
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      return parsedUser?.uid || parsedUser?.id || 'default-user';
    }
    return 'default-user';
  };
  const userId = getUserId();
  const pinTimeoutRef = useRef<number | null>(null);

  const setPinSession = () => {
    try {
      const key = PIN_SESSION_KEY_PREFIX + userId;
      sessionStorage.setItem(key, Date.now().toString());
      if (pinTimeoutRef.current) window.clearTimeout(pinTimeoutRef.current);
      pinTimeoutRef.current = window.setTimeout(() => {
        setIsVerified(false);
        try { sessionStorage.removeItem(key); } catch {}
      }, PIN_SESSION_TTL_MS);
    } catch {}
  };

  const tryAutoVerifyFromSession = () => {
    try {
      const key = PIN_SESSION_KEY_PREFIX + userId;
      const tsStr = sessionStorage.getItem(key);
      if (!tsStr) return false;
      const ts = parseInt(tsStr, 10);
      if (Number.isFinite(ts)) {
        const elapsed = Date.now() - ts;
        if (elapsed < PIN_SESSION_TTL_MS) {
          setIsVerified(true);
          const remaining = PIN_SESSION_TTL_MS - elapsed;
          if (pinTimeoutRef.current) window.clearTimeout(pinTimeoutRef.current);
          pinTimeoutRef.current = window.setTimeout(() => {
            setIsVerified(false);
            try { sessionStorage.removeItem(key); } catch {}
          }, remaining);
          return true;
        } else {
          sessionStorage.removeItem(key);
        }
      }
    } catch {}
    return false;
  };

  const storedPin = useMemo(() => {
    const existing = localStorage.getItem(PIN_STORAGE_KEY);
    return existing && existing.length > 0 ? existing : DEFAULT_PIN;
  }, []);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        setLoading(true);
        // Attempt auto-verify from session before any data fetch
        tryAutoVerifyFromSession();
        await migrateSingleNoteIfAny(userId);
        const list = await listEntries();
        if (!isMounted) return;
        setEntries(list);
      } catch (e) {
        if (!isMounted) return;
        setLoadError('Failed to load entries.');
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, [userId]);

  // No rich-text editor anymore

  const handleClose = () => {
    setShowModal(false);
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleVerify = async () => {
    try {
      const remotePin = await getRemotePin();
      const effectivePin = storedPin || remotePin;
      if (pinInput === (remotePin || effectivePin)) {
        setIsVerified(true);
        setPinError('');
        setPinSession();
      } else {
        setPinError('Incorrect PIN');
      }
    } catch {
      // Fallback to local stored pin if remote fails
      if (pinInput === storedPin) {
        setIsVerified(true);
        setPinError('');
        setPinSession();
      } else {
        setPinError('Incorrect PIN');
      }
    }
  };

  const handleSave = async () => {
    if (!editing) return;
    try {
      setSavedIndicator(false);
      if (editing.id) {
        await updateEntry(editing.id, {
          title: editing.title,
          username: editing.username,
          password: editing.password,
          notes: editing.notes || '',
          variant: editing.variant || 'primary',
        });
      } else {
        await addEntry({
          title: editing.title,
          username: editing.username,
          password: editing.password,
          notes: editing.notes || '',
          variant: editing.variant || 'primary',
        });
      }
      const list = await listEntries();
      setEntries(list);
      setSavedIndicator(true);
      setTimeout(() => setSavedIndicator(false), 1200);
      setEditing(null);
    } catch (e) {
      console.error('Failed to save entry', e);
    }
  };

  // Removed exec/editor logic

  const reloadEntries = async () => {
    setLoading(true);
    const list = await listEntries();
    setEntries(list);
    setLoading(false);
  };

  const copyToClipboard = async (text: string) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
    } catch (e) {
      console.warn('Copy failed', e);
    }
  };

  return (
    <>
      {showModal && (
        <div
          className="modal d-block"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1055,
          }}
          tabIndex={-1}
          onClick={handleBackdropClick}
        >
          <div
            className="modal-dialog modal-xl"
            style={{
              maxWidth: '95vw',
              width: '95vw',
              height: '95vh',
              margin: '2.5vh auto',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <div
              className="modal-content h-100"
              style={{
                backgroundColor: 'var(--bs-body-bg)',
                borderColor: 'var(--bs-border-color)',
                borderRadius: '0.75rem',
                border: '1px solid var(--bs-border-color)',
                boxShadow: 'var(--bs-dropdown-box-shadow)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div className="modal-header">
                <h5 className="modal-title">Password Manager</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={handleClose}></button>
              </div>
              <div
                className="modal-body p-4 h-100"
                style={{ flex: 1, overflow: 'auto', backgroundColor: 'var(--bs-body-bg)' }}
              >
                {!isVerified ? (
                  <div className="d-flex flex-column align-items-center justify-content-center h-100">
                    <div className="card w-100" style={{ maxWidth: 420 }}>
                      <div className="card-body">
                        <h5 className="mb-3">Enter PIN</h5>
                        <div className="mb-3">
                          <input
                            type="password"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            className="form-control form-control-lg"
                            placeholder="PIN"
                            value={pinInput}
                            onChange={(e) => setPinInput(e.target.value.replace(/\D/g, '').slice(0, 12))}
                          />
                          {pinError && <div className="text-danger mt-2">{pinError}</div>}
                        </div>
                        <button className="btn btn-primary btn-lg w-100" onClick={handleVerify} disabled={loading}>
                          {loading ? 'Loading…' : 'Unlock'}
                        </button>
                        <div className="text-muted mt-3" style={{ fontSize: 12 }}>
                          Default PIN is 1234. You can change it in Settings → Password Manager PIN.
                        </div>
                        {loadError && <div className="text-warning small mt-2">{loadError}</div>}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="d-flex flex-column h-100">
                    {/* Top bar: search and add */}
                    <div className="mb-3 d-flex flex-wrap gap-2 align-items-center justify-content-between">
                      <input
                        className="form-control form-control-lg"
                        style={{ maxWidth: '420px' }}
                        placeholder="Search by title, username, notes"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                      />
                      <button className="btn btn-primary btn-lg" onClick={() => setEditing({ title: '', username: '', password: '', notes: '', variant: 'primary' })}>Add Credential</button>
                    </div>

                    {/* Card list (no horizontal scroll on mobile) */}
                    <div className="flex-grow-1 overflow-auto" style={{ overflowX: 'hidden' }}>
                      <div className="row g-3">
                        {entries
                          .filter(e => {
                            const q = query.toLowerCase();
                            if (!q) return true;
                            return (
                              e.title.toLowerCase().includes(q) ||
                              e.username.toLowerCase().includes(q) ||
                              (e.notes || '').toLowerCase().includes(q)
                            );
                          })
                          .map(e => {
                            const id = e.id || '';
                            const reveal = revealedMap[id] || false;
                            return (
                              <div className="col-12 col-md-6 col-xl-4" key={id}>
                                <div className={`card h-100 card-flush shadow-sm ${getCardClassesForVariant(e.variant)}`} onClick={() => setViewing(e)}>
                                  <div className="card-body p-6">
                                    <div className="d-flex align-items-start justify-content-between mb-3">
                                      <div className="fw-bold fs-2" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{e.title || '(Untitled)'}</div>
                                      <div className="d-flex gap-2 flex-shrink-0">
                                        <button className="btn btn-sm btn-light" onClick={(ev) => { ev.stopPropagation(); setEditing(e); }}>
                                          Edit
                                        </button>
                                        <button className="btn btn-sm btn-light-danger" onClick={(ev) => { ev.stopPropagation(); setConfirmingDelete(e); }}>
                                          Delete
                                        </button>
                                      </div>
                                    </div>
                                    <div className="text-muted small mb-1">Username</div>
                                    <div className="mb-2" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{e.username || <span className="text-muted">(none)</span>}</div>
                                    <div className="text-muted small mb-1">Password</div>
                                    <div className="mb-2 d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                                      <span style={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }}>{maskPassword(e.password, reveal)}</span>
                                      <button
                                        className="btn btn-sm btn-light"
                                        title={reveal ? 'Hide' : 'Show'}
                                        onClick={(ev) => { ev.stopPropagation(); setRevealedMap(prev => ({ ...prev, [id]: !reveal })); }}
                                      >
                                        {reveal ? 'Hide' : 'Show'}
                                      </button>
                                    </div>
                                    {e.notes && <div className="text-muted small mb-1">Notes</div>}
                                    {e.notes && (
                                      <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={e.notes}>
                                        {e.notes.replace(/\n/g, ' ')}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>

                    {/* Editor overlay (custom, not Bootstrap modal, to avoid nested modal issues) */}
                    {editing && (
                      <>
                        <div
                          className="position-fixed top-0 start-0 w-100 h-100"
                          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}
                          onClick={() => setEditing(null)}
                        />
                        <div
                          className="position-fixed top-50 start-50 translate-middle w-100 px-3"
                          style={{ maxWidth: '650px', zIndex: 1061 }}
                        >
                          <div className="card shadow" onClick={(e) => e.stopPropagation()}>
                            <div className="card-header d-flex align-items-center justify-content-between">
                              <h5 className="mb-0">{editing.id ? 'Edit Credential' : 'New Credential'}</h5>
                              <button type="button" className="btn btn-sm btn-light" onClick={() => setEditing(null)}>Close</button>
                            </div>
                            <div className="card-body">
                              <div className="row g-3">
                                <div className="col-12">
                                  <label className="form-label">Title</label>
                                  <input className="form-control form-control-lg" value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} placeholder="e.g., GitHub" />
                                </div>
                                <div className="col-12 col-md-6">
                                  <label className="form-label">Username</label>
                                  <input className="form-control form-control-lg" value={editing.username} onChange={e => setEditing({ ...editing, username: e.target.value })} placeholder="username" />
                                </div>
                                <div className="col-12 col-md-6">
                                  <label className="form-label">Password</label>
                                  <input className="form-control form-control-lg" type="text" value={editing.password} onChange={e => setEditing({ ...editing, password: e.target.value })} placeholder="password" />
                                </div>
                                <div className="col-12">
                                  <label className="form-label">Notes</label>
                                  <textarea className="form-control" rows={4} value={editing.notes || ''} onChange={e => setEditing({ ...editing, notes: e.target.value })} placeholder="Optional"></textarea>
                                </div>
                                <div className="col-12">
                                  <label className="form-label">Color</label>
                                  <div className="d-flex flex-wrap gap-2">
                                    {variants.map((v: ThemeVariant) => (
                                      <button
                                        key={v}
                                        type="button"
                                        onClick={() => setEditing({ ...editing, variant: v })}
                                        className={`btn ${editing.variant === v ? 'border border-3 border-dark' : ''} ${getCardClassesForVariant(v)}`}
                                        style={{ width: 36, height: 36, borderRadius: 6 }}
                                        aria-label={`Variant ${v}`}
                                      />
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="card-footer d-flex justify-content-between">
                              <div className="text-success">{savedIndicator ? 'Saved' : ''}</div>
                              <div>
                                <button className="btn btn-light me-2" onClick={() => setEditing(null)}>Cancel</button>
                                <button className="btn btn-primary" onClick={handleSave} disabled={!editing.title}>Save</button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Viewing overlay: full details with tap-to-copy */}
                    {viewing && (
                      <>
                        <div
                          className="position-fixed top-0 start-0 w-100 h-100"
                          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}
                          onClick={() => setViewing(null)}
                        />
                        <div
                          className="position-fixed top-50 start-50 translate-middle w-100 px-3"
                          style={{ maxWidth: '600px', zIndex: 1061 }}
                        >
                          <div className="card shadow" onClick={(e) => e.stopPropagation()}>
                            <div className="card-header d-flex align-items-center justify-content-between">
                              <h5 className="mb-0">Details</h5>
                              <button type="button" className="btn btn-sm btn-light" onClick={() => setViewing(null)}>Close</button>
                            </div>
                            <div className="card-body">
                              <div className="mb-3">
                                <div className="text-muted small">Title</div>
                                <div className="fw-bold" role="button" onClick={() => copyToClipboard(viewing.title || '')} title="Click to copy">
                                  {viewing.title || '(Untitled)'}
                                </div>
                              </div>
                              <div className="mb-3">
                                <div className="text-muted small">Username</div>
                                <div role="button" onClick={() => copyToClipboard(viewing.username || '')} title="Click to copy" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                  {viewing.username || '(none)'}
                                </div>
                              </div>
                              <div className="mb-3">
                                <div className="text-muted small">Password</div>
                                <div role="button" onClick={() => copyToClipboard(viewing.password || '')} title="Click to copy" style={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }}>
                                  {viewing.password || '(none)'}
                                </div>
                              </div>
                              {viewing.notes && (
                                <div className="mb-3">
                                  <div className="text-muted small">Notes</div>
                                  <div role="button" onClick={() => copyToClipboard(viewing.notes || '')} title="Click to copy" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                    {viewing.notes}
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="card-footer d-flex justify-content-end">
                              <button className="btn btn-primary" onClick={() => setViewing(null)}>Close</button>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Delete confirmation (Metronic styled overlay, not Bootstrap modal) */}
                    {confirmingDelete && (
                      <>
                        <div
                          className="position-fixed top-0 start-0 w-100 h-100"
                          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}
                          onClick={() => setConfirmingDelete(null)}
                        />
                        <div
                          className="position-fixed top-50 start-50 translate-middle w-100 px-3"
                          style={{ maxWidth: '520px', zIndex: 1061 }}
                        >
                          <div className="card shadow" onClick={(e) => e.stopPropagation()}>
                            <div className="card-header d-flex align-items-center justify-content-between">
                              <h5 className="mb-0">Confirm Delete</h5>
                              <button type="button" className="btn btn-sm btn-light" onClick={() => setConfirmingDelete(null)}>Close</button>
                            </div>
                            <div className="card-body">
                              <p className="mb-0">Are you sure you want to delete <span className="fw-bold">{confirmingDelete.title || '(Untitled)'}</span>? This action cannot be undone.</p>
                            </div>
                            <div className="card-footer d-flex justify-content-end gap-2">
                              <button className="btn btn-light" onClick={() => setConfirmingDelete(null)}>Cancel</button>
                              <button
                                className="btn btn-danger"
                                onClick={async () => {
                                  if (!confirmingDelete?.id) return;
                                  await deleteEntry(confirmingDelete.id);
                                  await reloadEntries();
                                  setConfirmingDelete(null);
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {showModal && <div className="modal-backdrop fade show"></div>}
    </>
  );
};

export default PasswordManagerModal;


